import { visionLabApi } from '@/app/apiClient';
import type { ApiCallOptions } from '@/shared/api/client';
import type {
  GcsFolderBatchResponse,
  GcsFolderScanItem,
  GcsFolderScanResponse,
  SourceImportStatusItem,
  SourceImportWire,
  SourceImportsPageWire,
  TransferHistoryQueryBucket,
} from '../types/uploads';

/** Transport for the server-side external-import channel (`gs://` / `gdrive://`). */

export interface CreateSourceImportBody {
  importId: string;
  projectId: string;
  /** Already normalized to `gdrive://<fileId>` or `gs://bucket/key` (see `lib/driveUrl`). */
  sourceUrl: string;
}

export interface ScanGcsFolderBody {
  projectId: string;
  sourcePrefix: string;
}

export interface ImportGcsFolderSelectionBody extends ScanGcsFolderBody {
  idempotencyKey: string;
  items: Array<Pick<GcsFolderScanItem, 'sourceUrl' | 'generation' | 'sizeBytes'>>;
}

const seg = (id: string) => encodeURIComponent(id);

export interface DriveFolderFile {
  id: string;
  name: string;
  mimeType: string;
  size: string | null;
  modifiedTime: string | null;
}

export interface DriveFolderListing {
  files: DriveFolderFile[];
  truncated: boolean;
}

/** Project-authorized recursive listing of supported video and MCAP files in a Drive folder tree. */
export async function listDriveFolder(
  projectId: string,
  folderId: string,
  options: ApiCallOptions = {},
): Promise<DriveFolderListing> {
  return visionLabApi.get<DriveFolderListing>(
    `/source-imports/projects/${seg(projectId)}/gdrive/folders/${seg(folderId)}`,
    options,
  );
}

/** `POST /source-imports` → 202; the driver runs the import asynchronously. */
export async function createSourceImport(
  body: CreateSourceImportBody,
  options: ApiCallOptions = {},
): Promise<SourceImportWire> {
  return visionLabApi.post<SourceImportWire>('/source-imports', { ...options, json: body });
}

/** Fetch one caller-owned source import for ambiguous-create recovery. */
export async function getSourceImport(
  importId: string,
  options: ApiCallOptions = {},
): Promise<SourceImportWire> {
  return visionLabApi.get<SourceImportWire>(`/source-imports/${seg(importId)}`, options);
}

/** Read-only recursive Scan. It returns every structurally importable MCAP without history dedup. */
export async function scanGcsFolder(
  body: ScanGcsFolderBody,
  options: ApiCallOptions = {},
): Promise<GcsFolderScanResponse> {
  return visionLabApi.post<GcsFolderScanResponse>('/source-imports/gcs-folder-scans', {
    ...options,
    json: body,
  });
}

/** Persist and process one explicit selection of at most 100 objects from a prior Scan. */
export async function importGcsFolderSelection(
  body: ImportGcsFolderSelectionBody,
  options: ApiCallOptions = {},
): Promise<GcsFolderBatchResponse> {
  return visionLabApi.post<GcsFolderBatchResponse>('/source-imports/gcs-folder-selection-batches', {
    ...options,
    json: body,
  });
}

/**
 * Batch lifecycle status for many in-flight source imports in a single request. Replaces per-item polling
 * (source-import + intake-job + mcap-ingest) so a large folder import no longer fans out to one request
 * per item every couple of seconds. Callers send at most 200 ids per request (see the status
 * coordinator, which chunks); the response echoes only the ids the backend recognizes.
 */
export async function fetchSourceImportsStatus(
  importIds: string[],
  options: ApiCallOptions = {},
): Promise<{ items: SourceImportStatusItem[] }> {
  return visionLabApi.post<{ items: SourceImportStatusItem[] }>('/source-imports/status', {
    ...options,
    json: { importIds },
  });
}

/** The caller's source imports, independently paginated from direct-upload submissions. */
export async function listMySourceImports(
  uid: string,
  {
    limit,
    cursor,
    bucket,
  }: { limit?: number; cursor?: string; bucket?: TransferHistoryQueryBucket } = {},
  options: ApiCallOptions = {},
): Promise<SourceImportsPageWire> {
  return visionLabApi.get<SourceImportsPageWire>(`/source-imports/by-uploader/${seg(uid)}`, {
    ...options,
    searchParams: { limit, cursor: cursor || undefined, bucket },
  });
}
